import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Shield,
  Eye,
  Plus,
  Edit,
  Trash2,
  Download,
  UserCheck,
  UserX,
  Mail,
  X,
  Check,
  Flag,
  FileText,
  MessageSquare,
  Square,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Permission } from "@/types/permission";
import {
  groupPermissionsByModule,
  ParsedPermission,
  PossibleAction,
} from "@/lib/permissions";

interface UsersPermissionsCollapsibleProps {
  permissions: Permission[];
  isOpen?: boolean;
  className?: string;
}

type GroupedPermissions = Record<
  string,
  {
    moduleName: string;
    permissions: ParsedPermission[];
  }
>;

const ACTION_ICONS: Record<
  PossibleAction,
  React.ComponentType<{ className?: string }>
> = {
  read: Eye,
  create: Plus,
  update: Edit,
  delete: Trash2,
  export: Download,
  activate_account: UserCheck,
  deactivate_account: UserX,
  send_questionnaire_link: Mail,
  reject: X,
  confirm: Check,
  complete: Flag,
  amend: FileText,
  comment: MessageSquare,
  cancel: Square,
  start: Play,
};

const getModuleDisplayName = (
  moduleCode: string,
  t: (key: string) => string,
): string => {
  return t(`permissions.modules.${moduleCode}`) || moduleCode;
};

const getActionDisplayName = (
  action: string,
  t: (key: string) => string,
): string => {
  return t(`permissions.actions.${action}`) || action;
};

const getActionSortOrder = (action: string): number => {
  const standardOrder = ["read", "export", "create", "update", "delete"];
  const index = standardOrder.indexOf(action);
  return index !== -1 ? index : 999; // Standard actions first, then others
};

export const UsersPermissionsCollapsible: React.FC<
  UsersPermissionsCollapsibleProps
> = ({ permissions, isOpen = true, className }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(isOpen);

  const groupedPermissions = useMemo((): GroupedPermissions => {
    return groupPermissionsByModule(permissions, (module) =>
      getModuleDisplayName(module, t),
    );
  }, [permissions, t]);

  const totalPermissions = permissions.length;
  const totalModules = Object.keys(groupedPermissions).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-gray-600" />
                <CardTitle className="text-lg">
                  {t("permissions.permissions.title")}
                </CardTitle>
                <Badge variant="secondary" className="ml-2">
                  {totalPermissions} {t("permissions.permissions.granted")}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {totalModules} {t("permissions.permissions.modules")}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    open ? "rotate-180" : "rotate-0",
                  )}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-6">
              {Object.entries(groupedPermissions)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([moduleKey, moduleData]) => {
                  const grantedCount = moduleData.permissions.length;

                  return (
                    <div
                      key={moduleKey}
                      className="border border-gray-200 bg-gray-50/30 rounded-lg p-4 transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">
                          {moduleData.moduleName}
                        </h4>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="secondary"
                            className="bg-gray-100 text-gray-800 border-gray-200"
                          >
                            {grantedCount}{" "}
                            {t("permissions.permissions.granted")}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {moduleData.permissions
                          .sort((a, b) => {
                            const orderA = getActionSortOrder(a.action);
                            const orderB = getActionSortOrder(b.action);
                            if (orderA !== orderB) {
                              return orderA - orderB;
                            }
                            return a.action.localeCompare(b.action);
                          })
                          .map((permission) => (
                            <div
                              key={`${moduleKey}-${permission.action}`}
                              className="flex items-center gap-2 p-2 rounded-md border border-gray-200 bg-white transition-colors"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                {(() => {
                                  const IconComponent =
                                    ACTION_ICONS[
                                      permission.action as PossibleAction
                                    ];
                                  return IconComponent ? (
                                    <IconComponent className="h-4 w-4 text-gray-600" />
                                  ) : (
                                    <Shield className="h-4 w-4 text-gray-600" />
                                  );
                                })()}
                                <span className="text-sm font-medium text-gray-900">
                                  {getActionDisplayName(permission.action, t)}
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
            </div>

            {totalPermissions === 0 && (
              <div className="text-center py-8">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">
                  {t("permissions.permissions.noPermissions")}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {t("permissions.permissions.noPermissionsDescription")}
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default UsersPermissionsCollapsible;
